import { updateContractAddressId } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSlice'
import { subscriptionFormActionsV2 } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormV2Sagas'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectCharacteristicsCurrentContractActive,
  selectContractDetailsCurrentContractActive,
  selectCurrentContractActive,
  selectPreferencesContactSubscriptionData,
  selectProviderConfigCurrentContractActive,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSelectors'
import useRetrieveActiveContractIdAndType from '@/containers/SubscriptionFormPageNew/Layouts/useRetrieveActiveContractIdAndType'
import { useTranslation } from 'react-i18next'
import useAddress from '@/containers/SubscriptionFormPageNew/Components/Address/useAddress'
import { BUSINESS_VERTICAL_ENERGY } from '../../../../../SubscriptionForm/Common/constants'
import useAddressPodValidation from '@/containers/SubscriptionFormPageNew/Components/Address/useAddressPodValidation'
import { selectAuthData, selectEnergyAuthCommonData } from '@/containers/Login/AuthSelectors'
import { calculateOfferAvailability, retrieveNeededParamsForOffers } from '../../../../Contract/OfferTools'
import useEnedisPrefillData from '../../../../Consumption/useEnedisPrefillData'
import useMeterOwnerRule from '../../../../ElectricityMeter/useMeterOwnerRule'
import {
  selectAdditionalSaleDataByName,
  selectConfigCurrentContractActive,
  selectOfferConfigCurrentContractActive,
  selectPersonalContactSubscriptionData,
} from '../../../../../SubscriptionForm/SubscriptionFormSelectors'
import { updateContractConfigData } from '../../../../../SubscriptionForm/SubscriptionFormSlice'
import getError from '@/containers/SubscriptionFormPage/getError'
import useFormStatus from '@/containers/SubscriptionFormPageNew/SubscriptionForm/useFormStatus'
import { useEffect } from 'react'
import useAdditionalSaleFormData from '../../../../AdditionalSales/useAdditionalSaleFormData'
import { mandatoryTag } from '@/containers/SubscriptionFormPageNew/isMandatory'
import useLastHolderValidation from '@/containers/SubscriptionFormPageNew/Components/shared/useLastHolderValidation'
import useCadastralValidation from '@/containers/SubscriptionFormPageNew/Components/shared/useCadastralValidation'
import useBuildingPermitValidation from '@/containers/SubscriptionFormPageNew/Components/shared/useBuildingPermitValidation'

const useAddressSelector = (module, validations, saleName) => {
  const { t } = useTranslation()
  const { lang } = useSelector(selectAuthData)
  const { energyType, prestationType, cdfDelayed } = useSelector(selectCharacteristicsCurrentContractActive)
  const dispatch = useDispatch()
  const { id: currentContractId, type: businessVerticalType } = useRetrieveActiveContractIdAndType()
  const { addressId: additionalSaleAddressId } = useSelector(selectAdditionalSaleDataByName(saleName))
  const { addressId: contractAddressId } = useSelector(selectCurrentContractActive)
  const { activeAddress, updateActiveAddressHandler, addresses, addressesList } = useAddress()
  const { clientType } = useSelector(selectPreferencesContactSubscriptionData)
  const { providerId, isDebordement } = useSelector(selectContractDetailsCurrentContractActive)
  const { loadOffersUrl, loadOfferConfigurationUrl } = useSelector(selectEnergyAuthCommonData)
  const { lastName } = useSelector(selectPersonalContactSubscriptionData)
  const { offerZohoId, currentOfferId } = useSelector(selectOfferConfigCurrentContractActive)
  const { isPodMandatory = true } = useSelector(selectConfigCurrentContractActive)
  const { errors, testFieldValidity, isEditable } = useFormStatus()
  const { validateAddressPod } = useAddressPodValidation()
  const { prefillEnedisData } = useEnedisPrefillData()
  const { checkMeterOwner } = useMeterOwnerRule()
  const { updateLead } = useAdditionalSaleFormData()
  const { validateLastHolderData } = useLastHolderValidation()
  const { validateCadastralData } = useCadastralValidation()
  const { validateBuildingPermitData } = useBuildingPermitValidation()
  const providerConfig = useSelector(selectProviderConfigCurrentContractActive)

  const reloadOffers = zipcode => {
    if (businessVerticalType?.toLowerCase() !== BUSINESS_VERTICAL_ENERGY) return
    dispatch({
      type: subscriptionFormActionsV2.OFFER_MODULE_LOAD_OFFERS_V2,
      currentContractId,
      loadOffersUrl,
      loadOfferConfigurationUrl,
      params: retrieveNeededParamsForOffers({
        providerId,
        clientType,
        energyType,
        prestationType,
        isDebordement,
        zipcode,
      }),
    })
  }

  const updateAdditionalSaleAddressId = addressId => {
    updateLead({ addressId }, saleName)
  }

  const updateContractAddressDataHandler = addressId => {
    testFieldValidity('address_id', addressId, validations, currentContractId)
    const address = addresses[addressId] || {}
    const {
      inseeCode,
      zipcode,
      cityEligibility = {},
      electricityMeter = {},
      gasMeter = {},
      hasElectricityMeter = false,
      hasGasMeter = false,
    } = address
    const cityEligibilityIsLoaded = Object.keys(cityEligibility).length > 0
    const meterIdElec = electricityMeter.pdlNo || ''
    const meterIdGas = gasMeter.pceNo || ''

    dispatch(updateContractAddressId({ addressId, id: currentContractId }))

    validateAddressPod({
      contractId: currentContractId,
      addressId,
      energyType,
      hasElectricityMeter,
      hasGasMeter,
      isPodMandatory,
    })

    dispatch({
      type: subscriptionFormActionsV2.OFFER_MODULE_VALIDATION_CONTRACT_NOT_DUPLICATED,
      id: currentContractId,
      params: `energy_type=${energyType}&meter_id_elec=${meterIdElec}&meter_id_gas=${meterIdGas}`,
    })

    if (cityEligibilityIsLoaded) {
      reloadOffers(zipcode)
    }

    prefillEnedisData(energyType)
    checkMeterOwner(addressId, prestationType, electricityMeter.enedisAuth, electricityMeter.oldOccupant, lastName)
    validateLastHolderData(address.lastHolderData ?? {}, prestationType)
    validateCadastralData(address, providerConfig)
    validateBuildingPermitData(address, providerConfig)
    const { offerIsAvailable, offerErrorMessage, availableBillingType } = calculateOfferAvailability(
      electricityMeter.pdlEligibilityData?.offers ?? [],
      energyType,
      offerZohoId,
      currentOfferId,
      prestationType,
      cdfDelayed,
    )
    dispatch(
      updateContractConfigData({
        currentContractId,
        data: {
          offerIsAvailable,
          offerErrorMessage,
          availableBillingType,
        },
      }),
    )

    if (!inseeCode || energyType === 'propane' || lang === 'be' || lang === 'nl' || cityEligibilityIsLoaded) {
      return
    }

    dispatch({
      type: subscriptionFormActionsV2.CITY_ELIGIBILITY_CHECK_V2,
      inseeCode,
      addressId,
      offersParams: retrieveNeededParamsForOffers({
        providerId,
        clientType,
        energyType,
        prestationType,
        isDebordement,
        zipcode,
      }),
      loadOffersUrl,
      loadOfferConfigurationUrl,
      businessVerticalType,
    })
  }

  const addressSelectorHandler = addressId => {
    switch (module) {
      case 'client':
        updateActiveAddressHandler(addressId)
        return
      case 'contract':
        updateContractAddressDataHandler(addressId)
        return
      case 'additionalSales':
        updateAdditionalSaleAddressId(addressId)
        return
      default:
        return () => {}
    }
  }
  const selectedValue = (() => {
    switch (module) {
      case 'client':
        return activeAddress || null
      case 'contract':
        return contractAddressId || null
      case 'additionalSales':
        return additionalSaleAddressId || null
      default:
        return null
    }
  })()
  const allAddressErrors =
    getError('address_id', errors) ||
    getError('last_holder_data', errors) ||
    getError('cadastral_data', errors) ||
    getError('building_permit_data', errors)
  const addressError = module === 'client' ? null : allAddressErrors
  const label =
    module === 'client'
      ? t(['subscriptionForm:address_module.module_title_v2', 'subscriptionForm:address_module.module_title'])
      : mandatoryTag + t('subscriptionForm:address_module.selector_title')

  useEffect(() => {
    if (module === 'client' || !selectedValue) return
    addressSelectorHandler(selectedValue)
  }, [module])

  const { hasElectricityMeter = false, hasGasMeter = false } = addresses[contractAddressId] || {}

  useEffect(() => {
    if (module !== 'contract' || !contractAddressId) return
    validateAddressPod({
      contractId: currentContractId,
      addressId: contractAddressId,
      energyType,
      hasElectricityMeter,
      hasGasMeter,
      isPodMandatory,
    })
  }, [hasElectricityMeter, hasGasMeter, isPodMandatory])

  return {
    t,
    addressesList,
    addressSelectorHandler,
    selectedValue,
    addressError,
    isEditable: module === 'client' || module === 'additionalSales' ? true : isEditable,
    module,
    label,
  }
}

export default useAddressSelector
